import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { FORMA_PAGO, formatearMoneda, type EstadoFactura } from "@uirtus/shared";
import { useRef, useState } from "react";
import { Aviso, Boton, Confirmacion, Etiqueta, Tarjeta } from "../componentes/ui";
import { ErrorApi, OperacionEnProceso, api } from "../lib/api";
import { useEliminarPago, usePago, type FacturaPagoBreve } from "../lib/pagos";

const TONO_ESTADO: Record<EstadoFactura, "neutro" | "exito" | "alerta" | "peligro"> = {
  BORRADOR: "neutro",
  PENDIENTE_TIMBRE: "alerta",
  TIMBRADA: "exito",
  ERROR_TIMBRE: "peligro",
  CANCELACION_SOLICITADA: "alerta",
  CANCELADA: "peligro",
};

const formatearFechaHora = (iso: string) =>
  new Intl.DateTimeFormat("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));

export function PagoDetalle() {
  const { id } = useParams({ from: "/pagos/$id" });
  // Remonta al cambiar de id: dos rutas que matchean el mismo patrón
  // "/pagos/$id" no remontan el componente por sí solas.
  return <FichaPago key={id} id={id} />;
}

function FichaPago({ id }: { id: string }) {
  const navegar = useNavigate();
  const queryClient = useQueryClient();
  const { data: pago, isLoading } = usePago(id);
  const eliminar = useEliminarPago(id);

  const [aviso, setAviso] = useState<
    { tipo: "proceso" | "error" | "ok"; texto: string } | null
  >(null);
  const [confirmarBaja, setConfirmarBaja] = useState(false);
  const [urlXml, setUrlXml] = useState<string | null>(null);

  // Una sola clave de idempotencia por factura-de-pago, estable entre
  // reintentos: evita que un doble click o un reintento tras timeout
  // generen dos CFDIs. Solo se descarta ante un resultado definitivo.
  const clave = useRef<string | null>(null);
  const claveActual = (): string => {
    if (!clave.current) clave.current = crypto.randomUUID();
    return clave.current;
  };

  const timbrar = useMutation({
    mutationFn: (facturaPagoId: string) =>
      api<{ id: string; estado: EstadoFactura }>(`/facturas/${facturaPagoId}/timbrar`, {
        method: "POST",
        claveIdempotencia: claveActual(),
      }),
    onSuccess: () => {
      clave.current = null;
      setAviso({ tipo: "ok", texto: "Pago timbrado" });
      void queryClient.invalidateQueries({ queryKey: ["pagos"] });
    },
    onError: (err) => {
      if (err instanceof OperacionEnProceso) {
        // La clave se conserva: si el usuario insiste, la misma clave
        // devuelve el mismo resultado en vez de duplicar el comprobante.
        setAviso({
          tipo: "proceso",
          texto:
            "El PAC no respondió. El pago quedó en proceso y se resolverá solo en un par de minutos. No vuelvas a timbrar.",
        });
        void queryClient.invalidateQueries({ queryKey: ["pagos"] });
        return;
      }
      clave.current = null;
      setAviso({
        tipo: "error",
        texto: err instanceof ErrorApi ? err.message : "No se pudo timbrar el pago",
      });
    },
  });

  const descargarXml = useMutation({
    mutationFn: (facturaPagoId: string) =>
      api<{ url: string }>(`/facturas/${facturaPagoId}/xml`),
    onSuccess: (d) => setUrlXml(d.url),
    onError: (err) =>
      setAviso({
        tipo: "error",
        texto: err instanceof ErrorApi ? err.message : "No se pudo obtener el XML",
      }),
  });

  if (isLoading) return <p className="text-sm text-slate-500">Cargando…</p>;
  if (!pago) return <p className="text-sm text-slate-500">No se encontró el pago.</p>;

  const fp: FacturaPagoBreve | null = pago.facturaPago;

  return (
    <div className="space-y-4">
      <Link to="/pagos" className="text-sm text-marca-600 hover:underline">
        ← Pagos
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold">
            Pago de {pago.cliente.razonSocial}
          </h1>
          <p className="font-mono text-xs text-slate-500">{pago.cliente.rfc}</p>
        </div>
        {fp && <Etiqueta tono={TONO_ESTADO[fp.estado]}>{fp.estado.replace(/_/g, " ").toLowerCase()}</Etiqueta>}
      </div>

      {aviso && (
        <div
          className={
            aviso.tipo === "proceso"
              ? "rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
              : aviso.tipo === "error"
                ? "rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
                : "rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
          }
        >
          {aviso.texto}
        </div>
      )}

      <Tarjeta titulo="Datos del pago">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs uppercase text-slate-500">Fecha</dt>
            <dd>{formatearFechaHora(pago.fechaPago)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-slate-500">Forma de pago</dt>
            <dd>
              {pago.formaPago} ·{" "}
              {FORMA_PAGO[pago.formaPago as keyof typeof FORMA_PAGO] ?? "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-slate-500">Monto</dt>
            <dd className="font-mono">{formatearMoneda(pago.monto, pago.moneda)}</dd>
          </div>
          {pago.numOperacion && (
            <div>
              <dt className="text-xs uppercase text-slate-500">Núm. operación</dt>
              <dd className="font-mono">{pago.numOperacion}</dd>
            </div>
          )}
          {fp?.uuid && (
            <div className="col-span-2 sm:col-span-3">
              <dt className="text-xs uppercase text-slate-500">UUID fiscal</dt>
              <dd className="font-mono text-xs">{fp.uuid}</dd>
            </div>
          )}
        </dl>
      </Tarjeta>

      <Tarjeta titulo="Facturas aplicadas">
        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:bg-slate-800/50">
              <tr>
                <th className="px-3 py-2">Factura</th>
                <th className="px-3 py-2">Parcialidad</th>
                <th className="px-3 py-2 text-right">Saldo anterior</th>
                <th className="px-3 py-2 text-right">Pagado</th>
                <th className="px-3 py-2 text-right">Saldo insoluto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {pago.documentos.map((d) => (
                <tr key={d.facturaId}>
                  <td className="px-3 py-2 font-mono text-xs">
                    {d.factura.serie && d.factura.folio !== null
                      ? `${d.factura.serie}-${d.factura.folio}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2">{d.numParcialidad}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatearMoneda(d.impSaldoAnt, d.factura.moneda)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatearMoneda(d.impPagado, d.factura.moneda)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatearMoneda(d.impSaldoInsoluto, d.factura.moneda)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Tarjeta>

      {fp && (
        <div className="flex justify-end gap-2">
          {fp.estado === "BORRADOR" && (
            <Boton
              variante="secundario"
              onClick={() => setConfirmarBaja(true)}
              disabled={eliminar.isPending}
            >
              Eliminar
            </Boton>
          )}
          {(fp.estado === "BORRADOR" || fp.estado === "ERROR_TIMBRE") && (
            <Boton onClick={() => timbrar.mutate(fp.id)} disabled={timbrar.isPending}>
              {timbrar.isPending ? "Timbrando…" : "Timbrar"}
            </Boton>
          )}
          {fp.estado === "PENDIENTE_TIMBRE" && (
            <span className="self-center text-xs text-amber-600">resolviéndose…</span>
          )}
          {fp.estado === "TIMBRADA" && (
            <Boton
              variante="secundario"
              onClick={() => descargarXml.mutate(fp.id)}
              disabled={descargarXml.isPending}
            >
              {descargarXml.isPending ? "Obteniendo…" : "Descargar XML"}
            </Boton>
          )}
        </div>
      )}

      {urlXml && (
        <Aviso tono="exito">
          XML listo:{" "}
          <a href={urlXml} target="_blank" rel="noreferrer" className="underline">
            abrir enlace
          </a>{" "}
          (el enlace expira, si ya no funciona vuelve a pedirlo).
        </Aviso>
      )}

      <Confirmacion
        abierto={confirmarBaja}
        titulo="Eliminar pago"
        mensaje={
          <>
            Se va a borrar este pago y su comprobante en borrador. Solo es posible
            porque todavía no se ha timbrado.
          </>
        }
        textoConfirmar="Eliminar"
        peligroso
        ocupado={eliminar.isPending}
        alCancelar={() => setConfirmarBaja(false)}
        alConfirmar={() =>
          eliminar.mutate(undefined, {
            onSuccess: () => void navegar({ to: "/pagos" }),
            onError: (e) => {
              setConfirmarBaja(false);
              setAviso({ tipo: "error", texto: e.message });
            },
          })
        }
      />
    </div>
  );
}
